/**
 *
 * A utility module for managing authentication and authorization data
 * @module authManager
 */

import refreshAuthTokens from '@/handlers/refreshAuthTokens';
import fetchAuthTokens from '@/handlers/fetchOAuthTokens';

const jwt = require('jsonwebtoken');

// constants for localStorage keys
const AUTH_KEY = 'seenthis_deveportal_auth';
const JWT_TOKEN_KEY = 'jtw_token';

/**
* Validates that the authData object contains the required keys

* @param {Object} authData - The auth data object
*/
function isValidateAuthData(authData) {
  return ['access_token', 'id_token', 'refresh_token', 'expires_in'].every(str => !!authData[str]);
}

/**
 * Stores the authentication data object in localStorage after validating it
 * @throws {Error} When the provided authentication data object is invalid
 *
 * @param {object} authData - The authentication data object
 * @returns {object} The stored authentication data object
*/
export function setAuth(authData) {
  if (!isValidateAuthData(authData)) {
    throw new Error(`Invalid auth data ${JSON.stringify(authData)}`);
  }

  const storageObject = { ...authData };
  const jwtToken = jwt.decode(authData.id_token, { complete: true });
  storageObject.expires = new Date().getTime() + authData.expires_in * 1000;
  storageObject[JWT_TOKEN_KEY] = jwtToken;

  localStorage.setItem(AUTH_KEY, JSON.stringify(storageObject));

  return storageObject;
}

/**
 * Retrieves the stored authentication data object from localStorage
 *
 * @returns {object|null} The stored authentication data object or null if there is none
 */
export function getAuth() {
  const auth = localStorage.getItem(AUTH_KEY);
  if (auth) {
    const storageObject = JSON.parse(auth);
    if (storageObject.expires && storageObject.expires < Date.now()) {
      return null;
    }

    return storageObject;
  }
  return null;
}

/**
 * Checks if the stored authentication data is stale or has expired
 *
 * @returns {boolean} Whether the stored authentication data is stale or not
 */
export function isAuthStale() {
  const authData = getAuth();
  if (!authData) {
    return true;
  }

  const now = new Date().getTime();

  if (!authData.expires || now >= authData.expires) {
    return true;
  }

  return false;
}

/**
 * Calculates the time left for the authentication data to expire in seconds
 *
 * @returns {number} percentage of time left for the authentication data to expire
 */
export function isAuthExpiring() {
  const authData = getAuth();
  if (!authData) {
    return 0;
  }

  const now = new Date().getTime();

  return (authData.expires - now) / (authData.expires_in * 1000);
}

/**
 * Retrieves the stored JWT token from the authentication data object
 *
 * @returns {object|null} The stored JWT token or null if there is none
 */
export function getJwtToken() {
  const storageObject = getAuth();

  const jwtToken = storageObject ? storageObject[JWT_TOKEN_KEY] : null;

  return jwtToken;
}

/**
 * Returns the refresh token stored in localStorage if it exists, otherwise returns null.
 *
 * @returns {string|null} - The refresh token or null if not found
 */
export function getRefreshToken() {
  const authData = getAuth();
  return authData ? authData.refresh_token : null;
}

/**
 * Removes the stored authentication data object from localStorage
 */
export function removeAuth() {
  localStorage.removeItem(AUTH_KEY);
}

/**
 * Createa auth tokens from OAuth code recieved when user logs in
 * @throws {Error} When the provided authentication data object is invalid
 *
 * @param {string} authCode - OAuth code
 * @returns {bool}
 */
export async function createaAuthTokens(authCode) {
  const authData = await fetchAuthTokens(authCode);

  setAuth(authData);

  return true;
}

/**
 * use refresh token to get new auth tokens
 * @throws {Error} When the provided authentication data object is invalid
 *
 * @returns {bool}
 */
export async function refreshAuth() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  const authData = await refreshAuthTokens(refreshToken);

  setAuth({
    ...authData,
    refresh_token: refreshToken,
  });

  return true;
}

/**
 * Validates that the user's email domain is valid
 *
 * @param {string} email
 *
 * @returns {bool}
 */
const validateDomain = (email) => {
  if (!email) return false;

  const domain = email.split('@')[1];
  return process.env.VUE_APP_VALID_DOMAINS.split(',').includes(domain);
};

/**
 * Get user data to be used across the app
 * - refresh auth token if time is about to expire
 *
 * @returns {object}
 */
export function getUserData() {
  // remove auth token if it is stale
  if (isAuthStale()) {
    removeAuth();
  }
  // refresh auth token if time is about to expire
  if (isAuthExpiring() < 0.5) {
    refreshAuth();
  }

  const jwtToken = getJwtToken();
  const email = jwtToken ? jwtToken.payload.email : null;

  const userData = {
    JWT_token: jwtToken,
    isSignedIn: !isAuthStale(),
    validDomain: validateDomain(email),
    email,
  };

  return userData;
}
